import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import ModalDialog from '../ModalDialog/ModalDialog';
import JlrButton from '../JlrButton/JlrButton';
import { useResolutionScale } from '../../hooks/useResolutionScale';
import { DARK_MODE, REDIRECT_URLS, OPERATIONS } from '../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { addToMr } from '../../redux/actionCreators/majorRepair';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';
import { useRedirectToNextScreen } from '../../hooks';

/**
 * MoveToMr Component
 * A reusable button and dialog for station operators to send a vehicle directly to MR (POST /station/add-to-mr)
 */
export const MoveToMr = ({ partDetails, btnStyles }) => {
	const { scaleValue } = useResolutionScale();
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const allowedOperations = useSelector((state) => state?.operations?.allowedOperations || []);
	const dispatch = useDispatch();
	const { showMessageOverlay } = useMessageContextManager();
	const redirect = useRedirectToNextScreen();

	const [open, setOpen] = useState(false);
	const [reason, setReason] = useState('Quality issue found, needs rework');
	const [loading, setLoading] = useState(false);

	const asn = partDetails?.sequenceNumber || partDetails?.asn || partDetails?.serialNumber;

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	const handleConfirm = () => {
		if (!asn) return;

		setLoading(true);
		const closeOverlay = showMessageOverlay({
			contextText: 'Sending vehicle to Major Repair...',
			loading: true,
		});

		dispatch(
			addToMr(
				asn,
				reason,
				() => {
					setLoading(false);
					setOpen(false);
					closeOverlay();
					showMessageOverlay({
						contextText: 'Vehicle moved to Major Repair successfully',
						success: true,
					});

					setTimeout(() => {
						const isTransferDrop = allowedOperations.includes(OPERATIONS.TRANSFER_DROP);
						const targetUrl = isTransferDrop
							? '/transfer-drop'
							: REDIRECT_URLS[OPERATIONS.RECEIVE_PART];

						redirect(null, {
							operation: isTransferDrop ? OPERATIONS.TRANSFER_DROP : OPERATIONS.RECEIVE_PART,
							redirectUrl: targetUrl,
						});
					}, 1500);
				},
				(err) => {
					setLoading(false);
					closeOverlay();
					showMessageOverlay({
						contextText: 'Move to Major Repair Failed',
						info: err?.response?.data?.message || 'Failed to move vehicle to MR.',
						error: true,
					});
				},
			),
		);
	};

	return (
		<>
			<Button
				variant="contained"
				onClick={handleOpen}
				disabled={!asn}
				sx={{
					height: 56,
					px: 4,
					borderRadius: '12px',
					background: 'linear-gradient(90deg, #E65100 0%, #F57C00 100%)',
					color: '#ffffff',
					fontWeight: 600,
					fontSize: scaleValue(18),
					textTransform: 'none',
					'&:hover': {
						background: 'linear-gradient(90deg, #D84315 0%, #E65100 100%) !important',
					},
					'&.Mui-disabled': {
						background: 'linear-gradient(90deg, #d1d1d1 0%, #7a7a79 100%) !important',
						color: '#FFFFFF !important',
						opacity: 0.7,
					},
					...btnStyles,
				}}
			>
				Move to MR
			</Button>

			<ModalDialog
				open={open}
				title="Move Vehicle to Major Repair"
				onClose={handleClose}
				modalWidth={scaleValue(600)}
				modalHeight="auto"
			>
				<Box sx={{ p: 4, textAlign: 'center' }}>
					<Typography
						variant="h6"
						sx={{
							mb: 2,
							fontWeight: 600,
							color: themeMode === DARK_MODE ? '#fff' : '#222',
							fontSize: scaleValue(24),
						}}
					>
						Are you sure you want to pull ASN/VIN {asn} off the line and send to MR?
					</Typography>

					<TextField
						fullWidth
						multiline
						rows={3}
						label="Reason for Major Repair"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						sx={{
							my: 2,
							'& .MuiInputBase-root': {
								color: themeMode === DARK_MODE ? '#fff' : '#000',
							},
						}}
					/>

					<Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
						<JlrButton
							variant="outlined"
							size="medium"
							onClick={handleClose}
							disabled={loading}
							sx={{ minWidth: scaleValue(140), width: 'auto' }}
						>
							Cancel
						</JlrButton>
						<JlrButton
							variant="primary"
							size="medium"
							onClick={handleConfirm}
							disabled={loading}
							sx={{
								minWidth: scaleValue(160),
								width: 'auto',
								background: 'linear-gradient(90deg, #E65100 0%, #F57C00 100%)',
							}}
						>
							{loading ? 'Moving...' : 'Move to MR'}
						</JlrButton>
					</Box>
				</Box>
			</ModalDialog>
		</>
	);
};

export default MoveToMr;
